'use client';

import Image from 'next/image';
import { type JSX, useState } from 'react';

import type { ActionItemConfig } from '../types';
import SingleColumnMenu from './SingleColumnMenu';

interface DesktopNavActionsProps {
    items: ActionItemConfig[];
    darkHeader?: boolean;
}

function IconAction({
    item,
    darkHeader = false,
}: {
    item: ActionItemConfig & { type: 'icon' };
    darkHeader?: boolean;
}): JSX.Element {
    const [isOpen, setIsOpen] = useState(false);

    const chevronSvg = item.chevron ? (
        <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
        >
            <path
                d="M4 6L8 10L12 6"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    ) : null;

    if (!item.dropdown) {
        return (
            <a href={item.href} aria-label={item.ariaLabel} className="flex items-center gap-1 hover:opacity-80">
                <Image src={item.icon} alt="" width={24} height={24} className={darkHeader ? 'invert' : ''} />
                {item.label && (
                    <span className={`ml-2 text-base font-semibold ${darkHeader ? 'text-white' : 'text-gray-900'}`}>
                        {item.label}
                    </span>
                )}
                {chevronSvg}
            </a>
        );
    }

    return (
        <div
            className="relative"
            onMouseEnter={(): void => setIsOpen(true)}
            onMouseLeave={(): void => setIsOpen(false)}
        >
            <button type="button" className="flex items-center gap-1 hover:opacity-80" aria-label={item.ariaLabel}>
                <Image src={item.icon} alt="" width={24} height={24} className={darkHeader ? 'invert' : ''} />
                {item.label && (
                    <span className={`ml-2 text-sm font-normal ${darkHeader ? 'text-white' : 'text-gray-900'}`}>
                        {item.label}
                    </span>
                )}
                {chevronSvg}
            </button>

            {isOpen && (
                <>
                    {/*<div className="absolute left-0 right-0 z-40 h-7 bg-transparent" />*/}
                    <div className="absolute right-0 top-full z-50 mt-0 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-md whitespace-nowrap">
                        <SingleColumnMenu links={item.dropdown.links} />
                    </div>
                </>
            )}
        </div>
    );
}

export default function DesktopNavActions({ items, darkHeader = false }: DesktopNavActionsProps): JSX.Element {
    return (
        <div className="hidden items-center gap-6 min-[1255px]:flex">
            {items.map((item) => {
                if (item.type === 'link') {
                    return (
                        <a
                            key={item.href}
                            href={item.href}
                            className={`text-base font-normal hover:opacity-80 ${darkHeader ? 'text-white' : 'text-gray-900'}`}
                        >
                            {item.label}
                        </a>
                    );
                }

                if (item.type === 'cta') {
                    return (
                        <a
                            key={item.href}
                            href={item.href}
                            className="flex h-10 items-center justify-center rounded-[10px] bg-[#ffea6a] px-6 text-sm font-semibold text-gray-900 hover:bg-yellow-500"
                        >
                            {item.label}
                        </a>
                    );
                }

                return <IconAction key={item.href} item={item} darkHeader={darkHeader} />;
            })}
        </div>
    );
}
